import React, { useState } from "react";
import { fetchWrapper } from "utils/apiFetch";

// ── Config ───────────────────────────────────────────────────────────────────
// Adjust these to match how the rest of the app talks to the API.
const API_BASE = "https://pro.mokshiq.in";

const ENDPOINTS = {
  report: "/retailer_monthly_report",
  email:  "/email_monthly_report",
  bulk:   "/send_monthly_reports_bulk",
};

// ── Shared styles (same tokens as RechargeAPIDocs) ──────────────────────────
const styles = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: "#f1f5f9",
    minHeight: "100vh",
    padding: "28px 32px",
  },
  card: {
    background: "#fff",
    border: "0.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "20px 24px",
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    fontSize: 13,
    border: "0.5px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    fontFamily: "inherit",
  },
  button: (variant = "primary") => ({
    padding: "9px 16px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: variant === "primary" ? "none" : "0.5px solid #e2e8f0",
    background: variant === "primary" ? "#185FA5" : "#fff",
    color: variant === "primary" ? "#fff" : "#374151",
    cursor: "pointer",
  }),
  metricCard: {
    background: "#f8fafc",
    borderRadius: 10,
    padding: "14px 16px",
  },
  metricLabel: { fontSize: 12, color: "#64748b", marginBottom: 4 },
  metricValue: { fontSize: 20, fontWeight: 600, color: "#0f172a" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: {
    padding: "8px 10px", textAlign: "left", background: "#f8fafc",
    color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "0.5px solid #e2e8f0",
  },
  td: { padding: "9px 10px", borderBottom: "0.5px solid #f1f5f9", verticalAlign: "top" },
  badge: (color) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 6,
    fontSize: 11, fontWeight: 600, background: `${color}18`, color,
  }),
  sectionTitle: { fontSize: 15, fontWeight: 600, color: "#0f172a", marginBottom: 12 },
};

const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── API helpers ──────────────────────────────────────────────────────────────
async function apiPost(path, body, token) {
  const form = new FormData();
  Object.entries(body).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") form.append(k, v);
  });
  const res = await fetchWrapper(`${API_BASE}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return res;
}

// ── Report viewer ────────────────────────────────────────────────────────────
function ReportView({ data }) {
  if (!data) return null;
  if (data.status === false) {
    return (
      <div style={{ ...styles.card, color: "#A32D2D" }}>
        {data.message || "Could not load report."}
      </div>
    );
  }

  const { user, period, wallet_balance, summary, services, wallet_loads, transactions } = data;

  return (
    <div>
      {/* Header */}
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>{user?.name}</div>
            <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
              {user?.phone} · {user?.role}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12.5, color: "#64748b" }}>Period</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{period?.label}</div>
          </div>
        </div>
      </div>

      {/* Summary metrics */}
      <div style={{ ...styles.card }}>
        <div style={styles.sectionTitle}>Summary</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Business volume</div>
            <div style={styles.metricValue}>{money(summary?.total_business_volume)}</div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Commission earned</div>
            <div style={{ ...styles.metricValue, color: "#3B6D11" }}>
              {money(summary?.total_commission_earned)}
            </div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Wallet loaded</div>
            <div style={styles.metricValue}>{money(summary?.total_wallet_loaded)}</div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Transactions</div>
            <div style={styles.metricValue}>{summary?.total_transactions}</div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Success rate</div>
            <div style={styles.metricValue}>{summary?.success_rate}%</div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>Opening → closing</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginTop: 2 }}>
              {money(wallet_balance?.opening)} → {money(wallet_balance?.closing)}
            </div>
          </div>
        </div>
      </div>

      {/* Services breakdown */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Service breakdown</div>
        <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Service</th>
                <th style={styles.th}>Success</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Commission</th>
                <th style={styles.th}>Failed</th>
                <th style={styles.th}>Pending</th>
              </tr>
            </thead>
            <tbody>
              {(services || []).length === 0 && (
                <tr><td style={styles.td} colSpan={6}>No activity this month.</td></tr>
              )}
              {(services || []).map((s, i) => (
                <tr key={i}>
                  <td style={styles.td}>{s.service}</td>
                  <td style={styles.td}>{s.success_count}</td>
                  <td style={styles.td}>{money(s.success_amount)}</td>
                  <td style={{ ...styles.td, color: "#3B6D11" }}>{money(s.commission_earned)}</td>
                  <td style={styles.td}>
                    {s.failed_count > 0 ? <span style={styles.badge("#A32D2D")}>{s.failed_count}</span> : "—"}
                  </td>
                  <td style={styles.td}>
                    {s.pending_count > 0 ? <span style={styles.badge("#BA7517")}>{s.pending_count}</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wallet loads */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Wallet load history</div>
        <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Wallet</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Reference</th>
              </tr>
            </thead>
            <tbody>
              {(wallet_loads || []).length === 0 && (
                <tr><td style={styles.td} colSpan={4}>No wallet loads this month.</td></tr>
              )}
              {(wallet_loads || []).map((w) => (
                <tr key={w.id}>
                  <td style={styles.td}>{w.date}</td>
                  <td style={styles.td}>{w.wallet_type}</td>
                  <td style={styles.td}>{money(w.amount)}</td>
                  <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11 }}>{w.ref_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions (only present if include_transactions=true) */}
      {Array.isArray(transactions) && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            Transactions <span style={{ color: "#94a3b8", fontWeight: 400 }}>({transactions.length})</span>
          </div>
          <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "auto", maxHeight: 420 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Product</th>
                  <th style={styles.th}>Number</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Commission</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Ref</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr><td style={styles.td} colSpan={7}>No transactions this month.</td></tr>
                )}
                {transactions.map((t) => {
                  const isSuccess = /success/i.test(t.status || "");
                  const isFail = /fail/i.test(t.status || "");
                  const color = isSuccess ? "#3B6D11" : isFail ? "#A32D2D" : "#BA7517";
                  return (
                    <tr key={t.id}>
                      <td style={styles.td}>{t.date}</td>
                      <td style={styles.td}>{t.product || t.r_type}</td>
                      <td style={styles.td}>{t.number}</td>
                      <td style={styles.td}>{money(t.amount)}</td>
                      <td style={styles.td}>{money(t.commission)}</td>
                      <td style={styles.td}><span style={styles.badge(color)}>{t.status}</span></td>
                      <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11 }}>{t.ref_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function AdminMonthlyReport({ authToken, isAdmin = false }) {
  const now = new Date();

  const [userId, setUserId]   = useState("");
  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [year, setYear]       = useState(now.getFullYear());
  const [includeTxns, setIncludeTxns] = useState(true);

  const [loading, setLoading] = useState(false);
  const [report, setReport]   = useState(null);
  const [toast, setToast]     = useState("");

  const [bulkRoles, setBulkRoles] = useState("Retailer");
  const [bulkOnlyActive, setBulkOnlyActive] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const loadReport = async () => {
    if (!userId) return showToast("Enter a user ID first.");
    setLoading(true);
    setReport(null);
    try {
      const data = await apiPost(
        ENDPOINTS.report,
        { user_id: userId, month, year, include_transactions: includeTxns },
        authToken
      );
      setReport(data);
    } catch (e) {
      setReport({ status: false, message: "Network error while loading report." });
    } finally {
      setLoading(false);
    }
  };

  const emailReport = async () => {
    if (!userId) return showToast("Enter a user ID first.");
    try {
      const data = await apiPost(ENDPOINTS.email, { user_id: userId, month, year }, authToken);
      showToast(data.message || (data.status ? "Report emailed." : "Could not send email."));
    } catch {
      showToast("Network error while sending email.");
    }
  };

  const sendBulk = async () => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const data = await apiPost(
        ENDPOINTS.bulk,
        { month, year, roles: bulkRoles, only_with_activity: bulkOnlyActive },
        authToken
      );
      setBulkResult(data);
    } catch {
      setBulkResult({ status: false, message: "Network error while sending bulk reports." });
    } finally {
      setBulkLoading(false);
    }
  };

  const monthOptions = MONTH_NAMES.map((name, idx) => (
    <option key={idx} value={idx + 1}>{name}</option>
  ));
  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
    <option key={y} value={y}>{y}</option>
  ));

  return (
    <div style={styles.page}>
      <div style={{ maxWidth: 900 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>
          Monthly business report
        </div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
          Pull a member's monthly summary, email it to them, or send it to every active member at once.
        </div>

        {toast && (
          <div style={{ ...styles.card, background: "#EAF3DE", border: "0.5px solid #97C459", color: "#27500A" }}>
            {toast}
          </div>
        )}

        {/* Lookup controls */}
        <div style={styles.card}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1.3fr 1fr auto auto", gap: 12, alignItems: "end" }}>
            <div>
              <label style={styles.label}>User ID</label>
              <input
                style={styles.input}
                placeholder="e.g. 1042"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>
            <div>
              <label style={styles.label}>Month</label>
              <select style={styles.input} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {monthOptions}
              </select>
            </div>
            <div>
              <label style={styles.label}>Year</label>
              <select style={styles.input} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions}
              </select>
            </div>
            <button style={styles.button("primary")} onClick={loadReport} disabled={loading}>
              {loading ? "Loading…" : "Load report"}
            </button>
            <button style={styles.button("secondary")} onClick={emailReport}>
              Email to user
            </button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12.5, color: "#64748b" }}>
            <input
              type="checkbox"
              checked={includeTxns}
              onChange={(e) => setIncludeTxns(e.target.checked)}
            />
            Include full transaction list
          </label>
        </div>

        <ReportView data={report} />

        {/* Bulk send — admin only */}
        {isAdmin && (
          <div style={{ ...styles.card, marginTop: 8 }}>
            <div style={styles.sectionTitle}>Send to all active members</div>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr auto", gap: 12, alignItems: "end" }}>
              <div>
                <label style={styles.label}>Roles (comma separated)</label>
                <input
                  style={styles.input}
                  value={bulkRoles}
                  onChange={(e) => setBulkRoles(e.target.value)}
                  placeholder="Retailer, Distributer"
                />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#64748b" }}>
                <input
                  type="checkbox"
                  checked={bulkOnlyActive}
                  onChange={(e) => setBulkOnlyActive(e.target.checked)}
                />
                Only members with activity
              </label>
              <button style={styles.button("primary")} onClick={sendBulk} disabled={bulkLoading}>
                {bulkLoading ? "Sending…" : "Send now"}
              </button>
            </div>

            {bulkResult && (
              <div style={{ marginTop: 16 }}>
                {bulkResult.status === false ? (
                  <div style={{ color: "#A32D2D", fontSize: 13 }}>{bulkResult.message}</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    <div style={styles.metricCard}>
                      <div style={styles.metricLabel}>Sent</div>
                      <div style={{ ...styles.metricValue, color: "#3B6D11" }}>{bulkResult.sent_count}</div>
                    </div>
                    <div style={styles.metricCard}>
                      <div style={styles.metricLabel}>Failed</div>
                      <div style={{ ...styles.metricValue, color: "#A32D2D" }}>{bulkResult.failed_count}</div>
                    </div>
                    <div style={styles.metricCard}>
                      <div style={styles.metricLabel}>Skipped</div>
                      <div style={styles.metricValue}>{bulkResult.skipped_count}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}